// biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO - address later
import { Trash2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FieldSelect } from "@/components/filter-builder/field-select";
import { OperatorSelect } from "@/components/filter-builder/operator-select";
import { ValueEditor } from "@/components/filter-builder/value-editor";
import { Button } from "@/components/ui/button";
import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import {
  ALL_PRESETS,
  computeRelativeDateRange,
  DEFAULT_DATE_PRESET,
  type RelativeDatePreset,
} from "@/lib/date-utils";
import {
  type FilterOperator,
  isFilterComplete,
  type StructuredFilter,
} from "@/lib/filters";

export interface FilterRowProps {
  filter: StructuredFilter;
  availableFields: AvailableFilterField[];
  onChange: (updated: StructuredFilter) => void;
  onDelete: () => void;
}

/**
 * Detect if filter values match a known preset for today's date.
 * Returns the matching preset or null if no match.
 */
function detectActivePreset(
  filter: StructuredFilter,
  field: AvailableFilterField | undefined,
): RelativeDatePreset | null {
  if (!field) return null;

  const isDateField =
    field.dataType === "DATE" || field.dataType === "DATETIME";
  if (!isDateField) return null;

  // Only check BETWEEN and GTE operators which support presets
  if (filter.op !== "BETWEEN" && filter.op !== "GTE") return null;

  if (!Array.isArray(filter.values) || filter.values.length === 0) return null;

  // Check if values match any preset computed for today
  for (const preset of ALL_PRESETS) {
    const range = computeRelativeDateRange(preset);
    if (filter.op === "BETWEEN" && filter.values.length >= 2) {
      if (filter.values[0] === range.from && filter.values[1] === range.to) {
        return preset;
      }
    } else if (filter.op === "GTE") {
      if (filter.values[0] === range.from) {
        return preset;
      }
    }
  }

  return null;
}

export function FilterRow({
  filter,
  availableFields,
  onChange,
  onDelete,
}: FilterRowProps) {
  const currentField = useMemo(() => {
    return availableFields.find((f) => f.id === filter.field);
  }, [availableFields, filter.field]);

  // Derive activePreset from filter data - recompute when filter changes
  const [activePreset, setActivePreset] = useState<RelativeDatePreset | null>(
    () => detectActivePreset(filter, currentField),
  );

  // Sync activePreset when filter data changes (e.g., after deletion reorder)
  useEffect(() => {
    const detected = detectActivePreset(filter, currentField);
    setActivePreset(detected);
  }, [filter.field, filter.op, filter.values, currentField, filter]);

  const calculateAllowedOperators = (
    field: AvailableFilterField | undefined,
  ): FilterOperator[] => {
    if (!field) return [];
    return field.validOperators;
  };

  const allowedOps = useMemo(
    () => calculateAllowedOperators(currentField),
    [currentField, calculateAllowedOperators],
  );

  // Shared helper to compute default operator and values for a field
  const getFieldDefaults = (
    field: AvailableFilterField,
    ops: FilterOperator[],
  ): {
    op: FilterOperator;
    values: unknown[];
    isDatePreset: boolean;
  } | null => {
    if (ops.length === 0) return null;

    let defaultOp = ops[0];
    let values: unknown[] = [];
    let isDatePreset = false;

    const isDateField =
      field.dataType === "DATE" || field.dataType === "DATETIME";

    if (field.hasEnumValues && ops.includes("IN")) {
      defaultOp = "IN";
    } else if (field.dataType === "BOOLEAN" && ops.includes("EQ")) {
      defaultOp = "EQ";
      values = [true];
    } else if (isDateField) {
      // Default date fields to preset if BETWEEN/GTE available
      const defaultRange = computeRelativeDateRange(DEFAULT_DATE_PRESET);
      if (ops.includes("BETWEEN")) {
        defaultOp = "BETWEEN";
        values = [defaultRange.from, defaultRange.to];
        isDatePreset = true;
      } else if (ops.includes("GTE")) {
        defaultOp = "GTE";
        values = [defaultRange.from];
        isDatePreset = true;
      } else if (ops.includes("GT")) {
        defaultOp = "GT";
      }
    } else if (field.dataType === "NUMBER" && ops.includes("GT")) {
      defaultOp = "GT";
    } else if (field.dataType === "TEXT" && ops.includes("ILIKE")) {
      defaultOp = "ILIKE";
    } else if (ops.includes("EQ")) {
      defaultOp = "EQ";
    }

    return { op: defaultOp, values, isDatePreset };
  };

  // Handle invalid operator: reset to defaults when op is invalid for the current field
  // Dependencies are intentionally limited to prevent infinite loops - we only react to
  // field/op changes, not the entire filter object or onChange identity changes
  useEffect(() => {
    if (currentField) {
      // If current op is invalid for this field, reset to defaults
      const opIsInvalid =
        filter.op === null ||
        (allowedOps.length > 0 && !allowedOps.includes(filter.op));

      if (opIsInvalid) {
        const defaults = getFieldDefaults(currentField, allowedOps);
        onChange({
          ...filter,
          op: defaults?.op ?? null,
          values: defaults?.values ?? [],
        });
      }
    } else {
      // No field selected - clear op and values
      if (
        filter.op !== null ||
        (Array.isArray(filter.values) && filter.values.length > 0)
      ) {
        onChange({ ...filter, op: null, values: null });
      }
    }
  }, [
    currentField,
    filter.op,
    filter.field,
    allowedOps,
    filter,
    getFieldDefaults,
    onChange,
  ]);

  const handleFieldChange = (fieldId: string | null) => {
    const actualFieldId = fieldId === "" ? null : fieldId;

    if (actualFieldId) {
      const newSelectedField = availableFields.find(
        (f) => f.id === actualFieldId,
      );
      if (newSelectedField) {
        const opsForNewField = calculateAllowedOperators(newSelectedField);
        const defaults = getFieldDefaults(newSelectedField, opsForNewField);

        // activePreset will be synced by the detection effect based on the new filter values
        onChange({
          field: newSelectedField.id,
          op: defaults?.op ?? null,
          values: defaults?.values ?? [],
        });
      }
    } else {
      onChange({ field: null, op: null, values: null });
    }
  };

  const handleValueChange = (values: unknown[] | null) => {
    onChange({ ...filter, values });
  };

  // Handle relative date preset selection - sets both operator and values
  const handleRelativeDateSelect = (
    range: { from: string; to: string },
    preset: RelativeDatePreset,
  ) => {
    // Only set preset if we have a compatible operator to apply it
    if (allowedOps.includes("BETWEEN")) {
      setActivePreset(preset);
      onChange({ ...filter, op: "BETWEEN", values: [range.from, range.to] });
    } else if (allowedOps.includes("GTE")) {
      setActivePreset(preset);
      onChange({ ...filter, op: "GTE", values: [range.from] });
    }
    // If neither operator is available, don't set the preset (no-op)
  };

  // Handle operator selection - clears any active preset since user explicitly chose an operator
  const handleOperatorChange = (op: FilterOperator) => {
    setActivePreset(null);
    let newValues: unknown[] | null = [];
    if (currentField && currentField.dataType === "BOOLEAN" && op === "EQ") {
      newValues = [true];
    }
    onChange({ ...filter, op, values: newValues });
  };

  // Check if this is a date field
  const isDateField =
    currentField?.dataType === "DATE" || currentField?.dataType === "DATETIME";

  const isIncomplete = !isFilterComplete(filter);

  // Don't show value editor when a relative date preset is active
  const showValueEditor = currentField && filter.op && !activePreset;

  return (
    <div className="flex items-center gap-2">
      <FieldSelect
        fields={availableFields}
        value={filter.field || ""}
        onChange={handleFieldChange}
        className="h-9 text-sm"
      />
      {currentField && filter.op && allowedOps.length > 0 && (
        <OperatorSelect
          operators={allowedOps}
          value={filter.op}
          onChange={handleOperatorChange}
          className="h-9 text-sm"
          showRelativeDates={isDateField}
          onRelativeDateSelect={handleRelativeDateSelect}
          activePreset={activePreset}
        />
      )}
      {showValueEditor && (
        <ValueEditor
          field={currentField}
          filter={filter}
          onChange={handleValueChange}
          isIncomplete={isIncomplete}
        />
      )}
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <Trash2Icon className="h-4 w-4" />
      </Button>
    </div>
  );
}
